import { useEffect, useState, useMemo, useContext } from 'react';
import { ContentWrapper, DemoSectionWrapper, MessageBox } from './words.styles';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { SOURCES_TYPES } from '@/common/enums/source-types.enum';
import Loading from '@/common/components/Loading';
import {
	clearConversationFeature,
	loadConversationFeature,
} from '@/api/query/feature';
import ContantWordManager from './components/ContantWordManager';
import Header from './components/Header/Header';
import { useNavigate } from 'react-router-dom';
import { findWordLesson } from '@/api/query/word-lesson-type';
import ContinuePopUp from './components/ContinuePopUp';
import { IpStatsContext } from '@/common/contexts/IpStatsContext';
import { IP_STATS_ENUM } from '@/common/enums/ipStatsEnum';
import DailyTutorial from '../daily/components/DailyTutorial';

const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100vw;
	height: calc(var(--vh, 1vh) * 100 - 60px);
	overflow: hidden;
	text-align: center;
	margin: 0;
	padding: 0;
	background-color: white;

	@media (min-width: 600px) {
		flex-grow: 1;
		margin: 0;
		width: 100%;
		height: fit-content;
		position: relative;
	}
`;

const TUTORIAL_SHOWN_KEY = 'word-v2-tutorial';

const WordsV2 = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const [lessonContent, setSceneContent] = useState<{
		translation: string;
		_id: string;
	}>();
	const [conversation, setConversation] = useState<any>();
	const [isTutorialVisible, setIsTutorialVisible] = useState(false);
	const [showContinuePopUp, setShowContinuePopUp] = useState(false);
	const { handleSendIPstats } = useContext(IpStatsContext);

	const handleLoadLessonMutation = useMutation({
		mutationFn: (value: any) => findWordLesson(value),
		onSuccess: ({ data }) => {
			setSceneContent(data);
		},
	});

	const handleLoadConversation = useMutation({
		mutationFn: (payload: any) => loadConversationFeature(payload),
		onSuccess: ({ data }) => {
			setConversation(data);

			if (data.customerMessages?.some?.((m) => m.role === 'assistant')) {
				setShowContinuePopUp(true);
			}
		},
	});

	const handleClearConversation = useMutation({
		mutationFn: (payload: any) => clearConversationFeature(payload),
		onSuccess: ({ data }) => {
			setConversation(data);
		},
	});

	const lessonPayload = useMemo(
		() => ({ _id: id, type: 'WORDS', lessonId: id }),
		[id],
	);

	const handleRefreshFunction = () => {
		handleSendIPstats(IP_STATS_ENUM.APP_DAILY_NEW_CONV);

		setShowContinuePopUp(false);
		setConversation(undefined);

		handleClearConversation.mutate(lessonPayload);
	};

	const handleContinueConversation = () => {
		handleSendIPstats(IP_STATS_ENUM.APP_DAILY_CONTINUE_CONV);
		setShowContinuePopUp(() => false);
	};

	const handleCloseTutorial = () => {
		localStorage.setItem(TUTORIAL_SHOWN_KEY, 'true');
		setIsTutorialVisible(false);
	};

	useEffect(() => {
		const isTutorialShowedBefore = !!localStorage.getItem(TUTORIAL_SHOWN_KEY);
		setIsTutorialVisible(!isTutorialShowedBefore);
	}, []);

	useEffect(() => {
		window.document.title = `Synthi Ai - Scene`;

		if (!id) return;
		handleLoadConversation.mutate(lessonPayload);
		handleLoadLessonMutation.mutate(id);
	}, [id]);

	return (
		<>
			{handleLoadLessonMutation.isLoading ||
			handleLoadConversation.isLoading ||
			handleClearConversation.isLoading ? (
				<Loading />
			) : (
				<PageWrapper>
					<Header
						name={lessonContent?.translation}
						forWhat={lessonContent?._id}
						source={SOURCES_TYPES.WORD}
						onBack={() => navigate('/learn/words')}
						onTutorialClick={() => setIsTutorialVisible(true)}
					/>
					<DemoSectionWrapper>
						<ContentWrapper>
							<MessageBox>
								{lessonContent && conversation && (
									<ContantWordManager
										conversation={conversation}
										lesson={lessonContent}
									/>
								)}
								<div id='bottom-info' />
							</MessageBox>
						</ContentWrapper>
					</DemoSectionWrapper>
				</PageWrapper>
			)}
			{isTutorialVisible && <DailyTutorial handleClose={handleCloseTutorial} />}
			{!isTutorialVisible && showContinuePopUp && (
				<ContinuePopUp
					onContinueConversation={handleContinueConversation}
					onStartNewConversation={handleRefreshFunction}
				/>
			)}
		</>
	);
};

export default WordsV2;
